import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { createContext, type ReactNode, useContext } from "react";
import { Card } from "@/components/ui/card";
import { portalRpc } from "@/lib/portal-rpc";

type PortalContextValue = {
  hireFirstName: string;
  onboardingId: string;
  readOnly: boolean;
};

const PortalContext = createContext<PortalContextValue | null>(null);

export function usePortalContext(): PortalContextValue {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error("usePortalContext must be used inside <PortalShell>");
  return ctx;
}

export function usePortalContextOptional(): PortalContextValue | null {
  return useContext(PortalContext);
}

type Props = {
  children: ReactNode;
  /** When true, this is the `/onboarding` root and "not a hire" should fall
   *  back to a legacy element (typically the manager onboarding page). */
  fallback?: ReactNode;
};

export function PortalShell({ children, fallback }: Props) {
  const today = useQuery(portalRpc.getToday.queryOptions());

  if (today.isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Genuine failure (network / 5xx) — the "no onboarding" case is a normal 200
  // result handled below, not an error.
  if (today.error || !today.data) {
    return (
      <div className="flex h-full items-center justify-center py-24">
        <Card className="max-w-md p-8 text-center space-y-2">
          <h2 className="text-lg font-semibold">Portal indisponível</h2>
          <p className="text-sm text-muted-foreground">Tente novamente em alguns instantes.</p>
        </Card>
      </div>
    );
  }

  if (!today.data.ok) {
    // No active onboarding for the logged-in user. At the `/onboarding` root we
    // fall back to the manager view; otherwise this portal belongs to someone else.
    if (fallback) {
      return <>{fallback}</>;
    }
    return (
      <div className="flex h-full items-center justify-center py-24">
        <Card className="max-w-md p-8 text-center space-y-2">
          <h2 className="text-lg font-semibold">Este portal pertence a outro contratado</h2>
          <p className="text-sm text-muted-foreground">
            Você está logado como outro usuário. Faça login como o contratado indicado para ver este
            onboarding.
          </p>
        </Card>
      </div>
    );
  }

  const { payload } = today.data;
  const value: PortalContextValue = {
    hireFirstName: payload.hireFirstName,
    onboardingId: payload.onboardingId,
    readOnly: payload.isTerminal,
  };

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}
